import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { ButtonWhite } from '../Buttons/button-component';

// --- Generic inputs with size & variant ---
const sizeClassMap = { sm: '!text-base !py-1', lg: '!text-xl !py-2', xl: '!text-2xl !py-3' };
const variantClassMap = { default: 'input-component', bubble: 'input-component-bubble', search: 'input-search-component' };

// --- Validators ---
const phoneIlValidator = (required = true) => ({ type: 'tel', pattern: '^[+]?[\\d\\s()\\-]{10,}$', required, title: 'Phone number must be at least 10 digits. מספר טלפון חייב להיות לפחות 10 ספרות.' });
const emailValidator = (required = true) => ({ type: 'email', pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', required, title: 'נא להזין כתובת אימייל תקינה' });

// Updated to include .mp4 and video formats
const fileImageValidator = (required = true) => ({ 
    accept: '.png,.jpg,.jpeg,.svg,.mp4,.webm,.mov', 
    required, 
    title: 'נא לבחור קובץ תמונה או וידאו (PNG, JPG, SVG, MP4, WEBM)' 
});

const fileDesignValidator = (required = true) => ({ 
    accept: '.pdf,.png,.jpg,.jpeg,.svg,.mp4,.mov', 
    required, 
    title: 'נא לבחור קובץ עיצוב או מדיה תקין' 
});

// --- Components ---

const InputComponent = ({ type = 'text', value, onChange, placeholder, required = false, dir, className = '', size = 'lg', variant = 'default', label, id, ...rest }) => {
    const systemData = useSelector((state) => state.systemData || {});
    const effectiveDir = dir || systemData.dir || 'rtl';

    const input = (
        <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            dir={effectiveDir}
            className={`${variantClassMap[variant]} ${sizeClassMap[size] || ''} ${className}`}
            {...rest}
        />
    );

    if (!label) return input;

    return (
        <div className="flex flex-col gap-1 w-full">
            <label htmlFor={id} className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                {label}
            </label>
            {input}
        </div>
    );
};

const TextAreaComponent = ({ value, onChange, placeholder, required = false, dir, rows = 3, className = '', size = 'lg', variant = 'default', label, id, ...rest }) => {
    const systemData = useSelector((state) => state.systemData || {});
    const effectiveDir = dir || systemData.dir || 'rtl';
    const alignClass = effectiveDir === 'ltr' ? '!text-left' : '!text-right';

    const textarea = (
        <textarea
            id={id}
            rows={rows}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            dir={effectiveDir}
            className={`${variantClassMap[variant]} ${sizeClassMap[size] || ''} ${alignClass} resize-y ${className}`}
            {...rest}
        />
    );

    if (!label) return textarea;

    return (
        <div className="flex flex-col gap-1 w-full">
            <label htmlFor={id} className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                {label}
            </label>
            {textarea}
        </div>
    );
};

const FileInput = ({
    label = 'גרור ושחרר את הקובץ כאן',
    subLabel = 'PDF / PNG / JPG / SVG / MP4',
    accept = '.pdf,.png,.jpg,.jpeg,.svg,.mp4,.webm,.mov',
    required = false,
    title,
    value, 
    onFileSelected,
    className = '',
    ...rest
}) => {
    const fileInputRef = useRef(null);

    const handleFileSelected = (f) => {
        if (!f) return;
        if (typeof onFileSelected === 'function') {
            onFileSelected(f); 
        }
    };

    const handleSelectChange = (e) => {
        const f = e.target.files?.[0];
        handleFileSelected(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        handleFileSelected(f);
    };

    const handleDragOver = (e) => e.preventDefault();

    return (
        <div
            className={`rounded-2xl border-2 border-dashed border-blue-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 text-center hover:border-blue-400 transition-colors ${className}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            <div className="text-blue-800 dark:text-blue-200 font-semibold mb-1">{label}</div>
            <div className="text-sm text-blue-600/80 mb-3">{subLabel}</div>

            <ButtonWhite type="button" onClick={() => fileInputRef.current?.click()}>
                בחר קובץ
            </ButtonWhite>

            <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                required={required}
                title={title}
                onChange={handleSelectChange}
                className="hidden"
            />

            {/* Check if value exists and has a name (File object) */}
            {value && value.name && (
                <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300 flex items-center justify-center gap-2 animate-fadeIn">
                    <span className="font-bold">קובץ נבחר:</span> {value.name}
                </div>
            )}
        </div>
    );
};

export { InputComponent, TextAreaComponent, FileInput, phoneIlValidator, emailValidator, fileImageValidator, fileDesignValidator };